(ns manatee.components.answer
  (:require
   [clojure.set :refer [rename-keys]]
   [clojure.spec.alpha :as s]
   [clojure.string :as str]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.annotation.autosaving :as autosaving]
   [manatee.annotation.components :as annotation-components]
   [manatee.annotation.handlers :as handlers]
   [manatee.annotation.subs :as annotation-subs]
   [manatee.components.editors.component :as annotator]
   [manatee.components.editors.subs :as editors-subs]
   [manatee.components.media.component :as media]
   [manatee.components.question-text :as question-text]
   [manatee.domain.task :as task]
   [manatee.keyboard.handlers :as keyboard]
   [manatee.scoring-criteria :refer [remove-certain-scoring-criteria]]
   [manatee.spec.core :as spec]
   [manatee.ui.subs :as ui-subs]
   [manatee.user.components :as user-components]
   [re-frame.core :as rf]
   [rothko.accordion.component :refer [accordion]]
   [rothko.audio.wave-player.component :refer [wave-player]]
   [rothko.component.core :refer [defcomp]]
   [rothko.editor.document.script.spec :as script-spec]
   [rothko.form2.checkbox.component :as checkbox]
   [rothko.form2.component :as f]
   [rothko.form2.radio.component :as radio]
   [rothko.form2.select.core :as select]
   [rothko.form2.select.single-select.component :as single-select]
   [rothko.form2.validation :as validation]
   [rothko.form2.validation-message.component :as validation-message]
   [rothko.panel.component :refer [panel]]
   [utility-belt.pure :refer [parse-int]]))

(defn- question-text-hidden-by-default?
  "All Read Aloud (Part 2) questions, regardless of task (except EST Schools)
   should have the question text hidden by default, but be revealable by the
   annotator."
  [part-number account]
  (and (= part-number 2)
       (not (= :metrica-est-schools account))))

(defn waveplayer-component
  [{:keys [id on-destroy-handler on-finish-handler on-pause-handler on-play-handler on-ready-handler on-error-handler
           on-load-handler rubric part-number account text source-url segment-annotation-field time-markers-field
           autosaving-disabled? item]}]
  (let [title [accordion
               {:appearance #{:primary}
                :open (if (question-text-hidden-by-default? part-number account)
                        #{}
                        #{:question})
                :items
                [(when (seq text)
                   {:key :question
                    :title [:h6 "Question"]
                    :content [:div.default-style-in-markdown (question-text/with-markdown text)]})]}]
        event-opts {:item item
                    :autosaving-disabled? autosaving-disabled?}
        playhead-autosaving? (and segment-annotation-field
                                  (not autosaving-disabled?))]
    ^{:key id}
    [wave-player
     {:allow-playback-rate-change? (not (nil? segment-annotation-field)) ; Only for transcript-annotation tasks.
      :appearance (into #{:primary} (when segment-annotation-field [:diarisation]))
      :audio-src source-url
      :component-did-mount #(keyboard/make-init-waveplayer id %)
      :component-will-unmount #(rf/dispatch [::keyboard/clear-keyboard])
      :id id
      :on-destroy-handler on-destroy-handler
      :on-finish-handler on-finish-handler
      :on-load-handler on-load-handler
      :on-pause-handler on-pause-handler
      :on-play-handler on-play-handler
      :on-ready-handler on-ready-handler
      :on-error-handler on-error-handler
      :options {:cursor-time {:hideOnBlur true
                              :hideOnScroll false}}
      :plugins (into [:cursor-time] (when segment-annotation-field [:regions]))
      :title title
      :regions (when segment-annotation-field
                 @(rf/subscribe [::annotation-subs/segments id]))
      :reload-regions? (when (and segment-annotation-field
                                  @(rf/subscribe [::annotation-subs/reload-segments? id]))
                         (rf/dispatch [::handlers/reset-reload-segments id])
                         true)
      :on-regions-change (when segment-annotation-field
                           #(rf/dispatch [::handlers/segments-changed event-opts %]))
      :region-id-to-select (when segment-annotation-field
                             @(rf/subscribe [::annotation-subs/segment-id-to-select id]))
      :colour-palette (:speakers segment-annotation-field)
      :colour-id-key :speaker
      :time-markers (when time-markers-field
                      @(rf/subscribe [::annotation-subs/time-markers id]))
      :on-time-markers-change (when time-markers-field
                                #(rf/dispatch [::handlers/time-markers-changed event-opts %]))
      :time-marker-types (:time-marker-types time-markers-field)
      :playhead-position (when playhead-autosaving?
                           @(rf/subscribe [::autosaving/autosaved-playhead id]))
      :on-playhead-move #(do (rf/dispatch [::annotator/update-playhead %])
                             (when playhead-autosaving? (rf/dispatch [::autosaving/autosave-playhead item %])))
      :title-prefix rubric}]))

(defcomp answer-waveplayer
  {:spec ::spec/answer-waveplayer}
  [{{{{part-number :number
       :keys [rubric short-rubric] :as _part} :part
      :keys [text]} :question
     :keys [source-url id]} :item
    :keys [waveplayer item task-config autosaving-disabled?]
    :as _props}
   & _children] ; required by defcomp
  (let [{:keys [account-ids]} @(rf/subscribe [::ui-subs/config])]
    (waveplayer-component
     {:id id
      :on-destroy-handler (:on-destroy waveplayer)
      :on-finish-handler (:on-finish waveplayer)
      :on-load-handler (:on-load waveplayer)
      :on-pause-handler (:on-pause waveplayer)
      :on-play-handler (:on-play waveplayer)
      :on-ready-handler (:on-ready waveplayer)
      :on-error-handler (:on-error waveplayer)
      :rubric (let [long-rubric? (and rubric (task/modifier task-config :show-long-rubric?))
                    short-rubric? (task/modifier task-config :show-short-rubric?)]
                (cond long-rubric? [:div.default-style-in-markdown (question-text/with-markdown rubric)]
                      short-rubric? short-rubric))
      :part-number part-number
      :account (task/account task-config account-ids)
      :text text
      :source-url source-url
      :segment-annotation-field (task/segment-annotation-field task-config)
      :time-markers-field (task/time-markers-field task-config)
      :autosaving-disabled? autosaving-disabled?
      :item item})))

(defn question-content?
  [question]
  (boolean
   (or
    (-> question :text seq)
    (-> question :part :rubric seq)
    (-> question :part :short-rubric seq))))

(defcomp question-content
  {:spec ::spec/question-content}
  [{{{{:keys [rubric short-rubric]} :part} :question :as item} :item
    :as _props}]
  [:div
   (when-let [rubric-text (or short-rubric rubric)]
     [:p rubric-text])
   [question-text/question-text item]])

(s/def ::answer-panel
  (s/or
   :annotation-view
   (s/cat
    :props (s/keys
            :req-un [::spec/item])
    :score-panel vector?
    :ai-hybrid? (s/? vector?)
    :back-button vector?)

   :edit-view
   (s/cat
    :props (s/keys
            :opt-un [::spec/item])
    :score-panel vector?
    :ai-hybrid? (s/? vector?))))

(defn media?
  [question]
  (or (:audio-url question)
      (:image-urls question)))

(defn previous-score-content
  [{:keys [user data]}]
  [:div
   (when user
     [:p [:strong (user-components/full-name user)] " assigned these scores:"])
   (into [:div]
         (for [[criteria-name criteria-value]
               (sort-by key (dissoc data :account-id :item-id :status :task-config-id :manatee-version))]
           [:p (str/replace ((fnil name "missing criteria name") criteria-name) #"-" " ") ": "
            [:strong criteria-value]]))])

(defn previous-transcription-content
  [{:keys [user]}]
  [:div
   [:p [:strong (user-components/full-name user)] " previously transcribed this item."]])

(defn previous-annotations-content
  [previous-annotations]
  (into [:<>]
        (for [{:keys [task-config-id] :as previous-annotation} previous-annotations
              :let [task-config @(rf/subscribe [::task-config-subs/task-config task-config-id])]]
          [:div.annotation__previous
           (if (task/scoring? task-config)
             [previous-score-content previous-annotation]
             [previous-transcription-content previous-annotation])])))

(defn feedback-content
  [{:keys [comment] :as feedback}]
  (if-let [reported-comment (get-in feedback [:edit :report :comment])]
    [:<>
     [:div.feedback__reported-comment
      [:h5 "Reported"]
      [:em reported-comment]]
     [:div.feedback__comment
      [:h5 "Comment"]
      [:em comment]]]
    [:div.feedback__comment.no-report
     [:em comment]]))

(defn- timestamped-audio-event-handler
  "Returns a function that will dispatch `event` (with `timestamp` conjoined)
   when called."
  [event]
  (fn [timestamp]
    (rf/dispatch (conj event timestamp))))

(defn audio-event-with-duration-handler
  "Returns a function that will dispatch `event` (with `duration` conjoined)
   when called."
  [event]
  (fn [duration]
    (rf/dispatch (conj event duration))))

(defn audio-event-handler
  "Returns a function that will dispatch `event` when called."
  [event]
  (fn []
    (rf/dispatch event)))

(defn make-answer-props
  [{:keys [id medium source-url] :as item} task-config scoring-task? {:keys [spellcheck-disabled?] :as props}]
  (let [script-annotation-field (task/script-annotation-field task-config)
        segment-annotation-field (task/segment-annotation-field task-config)]
    (cond-> (assoc props
                   :item item
                   :scoring-task? scoring-task?
                   :task-config task-config
                   :spellcheck-disabled? (or spellcheck-disabled?
                                             (not script-annotation-field))
                   :segment-annotation? (boolean segment-annotation-field))
      (and (not (task/modifier task-config :hide-media?))
           (not (task/error-annotation? task-config))
           (= :speech medium)
           source-url)
      (assoc :waveplayer {:on-destroy (timestamped-audio-event-handler [::handlers/answer-audio-destroyed id])
                          :on-finish (timestamped-audio-event-handler [::handlers/answer-audio-ended id])
                          :on-pause (timestamped-audio-event-handler [::handlers/answer-audio-paused id])
                          :on-play (timestamped-audio-event-handler [::handlers/start-answer-audio id])
                          :on-ready (audio-event-with-duration-handler [::handlers/answer-audio-ready id])}))))

(defcomp answer-panel
  {:spec ::answer-panel}
  [{{:keys [duration-string feedback part previous-annotations question task-documents] :as item} :item
    :keys [waveplayer task-config scoring-task?]
    :as props}
   & children]
  (cond-> [panel {:appearance #{:primary}
                  :class #{:allow-overflow}}]
    (seq task-documents) (conj (annotation-components/info-links task-documents (:id task-config)))
    feedback (conj [:h5.feedback__warning "[Feedback available]"])
    item (conj [accordion
                {:appearance #{:primary}
                 :open (cond-> #{:feedback :previous-annotations :question :score}
                         (nil? (task/modifier task-config :answer-close?)) (conj :answer)
                         (or (= part 5)
                             (task/modifier task-config :media-open?)) (conj :media))
                 :items [(when (and (media? question)
                                    (not (task/error-annotation? task-config)))
                           {:key :media
                            :title [:h4 "Media"]
                            :content [media/component item]})
                         (when previous-annotations
                           {:key :previous-annotations
                            :title [:h4 "Previous Annotations"]
                            :content [previous-annotations-content previous-annotations]})
                         (cond
                           waveplayer {:key :answer
                                       :title [:h4 "Answer"]
                                       :content (cond-> [:div [answer-waveplayer props]]
                                                  duration-string (conj [:p duration-string]))}
                           (and (question-content? question)
                                (not (:hide-question? props))) {:key :question
                                                                :title [:h4 "Question"]
                                                                :content [question-content props]})
                         (let [answer-text (-> item :answer :text)]
                           (when (and scoring-task? answer-text)
                             {:key :answer
                              :title [:h4 "Answer"]
                              :content [:p.answer-text answer-text]}))
                         (when feedback
                           {:key :feedback
                            :title [:h4 "Feedback"]
                            :content [feedback-content feedback]})]}])
    :always (into children)))

(defn score-drop-down
  [score-id score-init score-options]
  {:appearance #{:primary :open-above}
   ::f/name score-id
   ::f/placeholder "Score..."
   ::f/initial-value score-init
   ::select/options score-options
   ::validation/spec ::spec/answer-score
   ::validation/messages {::spec/answer-score "You must select a score"}})

(defmulti score (fn [{:keys [task-config]}]
                  (get-in task-config [:data :answer :fields 0 :field-type])))

(defmethod score nil ; No field.
  [_])

(defmethod score :drop-down
  [{:keys [task-config scorable]
    {:keys [edit]} :scorable}]
  (let [answer-score-id (task/answer-score-id task-config)
        init-value-k (get edit answer-score-id)
        init-value (if init-value-k (name init-value-k) nil)]
    [:div.annotation__score
     [f/form
      (annotation-components/form-settings scorable)
      {answer-score-id
       (score-drop-down answer-score-id init-value (task/score-options task-config answer-score-id))}
      (fn [_ selection]
        [:form
         [:div.form-row
          [single-select/field (get selection answer-score-id)]]])]]))

(defn order-scoring-fields
  "If the scoring fields include checkbox options
   then sort the scoring fields so that radio-buttons appear before checkboxes"
  [field-props]
  (let [cb-props (filter ::checkbox/option (vals field-props))]
    (if (empty? cb-props)
      field-props
      (sort-by (comp ::checkbox/option second) (fn [key1 key2] (and (not key1) (not key2))) field-props))))

(defmethod score :scoring
  [{:keys [disabled? task-config auto-scores]
    {:keys [edit question feedback] :as scorable} :scorable}]
  (let [edit (or edit (:edit feedback))
        scoring-field (-> task-config :data :answer :fields first)
        show-ai-hybrid? (task/modifier task-config :show-ai-hybrid?)
        scoring-criteria (->> (:criteria scoring-field)
                              (map #(rename-keys % {:value :name}))
                              (map #(update % :name keyword)))
        scoring-criteria-fields (if (task/scoring? task-config)
                                  (remove-certain-scoring-criteria question task-config scoring-criteria)
                                  scoring-criteria)

        ; See https://btsservice.atlassian.net/browse/ELIT-3679 and related
        ; Slack discussion. However, we leave the key rename in place since
        ; submissions assessed prior to the AMA deployment will still have the
        ; old key.
        highlighting (rename-keys auto-scores {:language-resources :language-resource})
        show-analytic-scores? (get-in task-config [:data :modifiers :show-analytic-scores?])
        base-fields (into {}
                          (comp (remove nil?)
                                (map (fn [{:keys [optional] :as field}]
                                       (assoc field
                                              :appearance #{:horizontal}
                                              ::validation/spec (if optional ::spec/optional-score ::spec/score)
                                              ::validation/messages {(if optional ::spec/optional-score ::spec/score) "You must select a value"}
                                              ::f/coercer (comp parse-int str) ; Needed to deal with both int & string (also handles `nil` value).
                                              ::radio/options (for [option (:score-range field
                                                                                         (:score-range scoring-field))]
                                                                {:value option :label (str option)})
                                              ::radio/highlight (when (and auto-scores show-analytic-scores?)
                                                                  {::f/value ((:name field) highlighting)
                                                                   ::radio/neighbours? true}))))
                                (map-indexed #(assoc %2 :tab-index (inc %)))
                                (map #(rename-keys % {:name ::f/name :label ::f/label})) ;;rothkoify keys
                                (map (juxt ::f/name identity)))
                          scoring-criteria-fields)
        fields (if show-ai-hybrid?
                 (assoc base-fields :ai-hybrid? (annotation-components/ai-hybrid-checkbox (:ai-hybrid? edit)))
                 base-fields)]
    [:div.annotation__score
     [f/form
      (merge
       (annotation-components/form-settings scorable)
       {::f/disabled? disabled?
        ::f/initial-value edit})
      fields
      (fn [_form-props field-props]
        (into [:form]
              (map (fn [[_ field]]
                     (if (contains? field ::checkbox/option)
                       [checkbox/field field]
                       [radio/field field]))
                   (order-scoring-fields field-props))))]]))

(defmethod score :labelled-scoring
  [{:keys [disabled? task-config]
    {:keys [edit feedback] :as scorable} :scorable}]
  (let [edit (or edit
                 (:edit feedback))
        scoring-fields (into {}
                             (comp (remove nil?)
                                   (map #(assoc %
                                                :appearance #{:horizontal}
                                                ::validation/spec ::spec/labelled-score
                                                ::validation/messages {::spec/labelled-score "You must select a value"}))
                                   (map-indexed #(assoc %2
                                                        :tab-index (inc %1)))
                                   (map #(rename-keys % {:name ::f/name
                                                         :label ::f/label
                                                         :options ::radio/options})) ;;rothkoify keys
                                   (map (juxt ::f/name identity)))
                             (->> (get-in task-config [:data :answer :fields 0 :criteria])
                                  (map #(rename-keys % {:value :name}))
                                  (map #(update % :name keyword))))]
    [:div.annotation__score
     [f/form
      (merge (annotation-components/form-settings scorable)
             {::f/disabled? disabled?
              ::f/initial-value edit})
      scoring-fields
      (fn [_form-props field-props]
        [:form
         (for [[_ field] field-props]
           ^{:key (::f/name field)}
           [radio/field field])])]]))

(defn editor-form-body
  [answer-score-id answer-score-options answer-score-init field-name spec show-ai-hybrid? ai-hybrid-value]

  (let [body {field-name
              {::f/name field-name
               ::validation/spec spec
               ::validation/messages {spec (str "Not a valid " (name spec))}}}]
    (cond-> body
      answer-score-id
      (assoc answer-score-id (score-drop-down answer-score-id answer-score-init answer-score-options))

      show-ai-hybrid?
      (assoc :ai-hybrid? (annotation-components/ai-hybrid-checkbox ai-hybrid-value)))))

(defn edit-score
  [edit answer-score-id]
  (let [score-key (get edit answer-score-id)]
    (if score-key (name score-key) nil)))

(defn editor-form-fn
  [component spec]
  (fn editor-form
    [{:keys [item document on-mount on-transform on-change-view initial-view hide-toolbar? read-only?
             custom-markers immutable-text? show-ai-hybrid? answer-score-id answer-score-options field-name editor-event-opts]
      {:keys [edit]} :item :as _props}]
    [f/form
     (annotation-components/form-settings item)
     (editor-form-body answer-score-id answer-score-options (edit-score edit answer-score-id) field-name spec show-ai-hybrid? (:ai-hybrid? edit))
     (fn [_ {{:keys [::f/set-value!]} field-name :as params}]
       (let [ai-hybrid? (:ai-hybrid? params)
             spelling-dictionary @(rf/subscribe [::editors-subs/spelling-dictionary])
             answer-score-init (get params answer-score-id)]
         [:form
          [component
           {:spelling-dictionary spelling-dictionary
            :document document
            :on-transform on-transform
            :on-mount on-mount
            :on-change-view on-change-view
            :initial-view initial-view
            :hide-toolbar? hide-toolbar?
            :custom-markers custom-markers
            :immutable-text? immutable-text?
            :read-only? read-only?
            :init set-value!
            :on-blur set-value!
            :editor-event-opts editor-event-opts}]
          (when-not read-only?
            [validation-message/component (get params field-name)])
          (when answer-score-id
            [:div.text-annotation-scoring
             [single-select/field answer-score-init]])
          (when ai-hybrid?
            [:div
             [checkbox/field ai-hybrid?]])]))]))

(def transcript-editor-form
  (editor-form-fn annotator/transcript-annotation-component ::script-spec/transcript))

(def error-editor-form
  (editor-form-fn annotator/error-annotation-component ::script-spec/transcript))

(def script-editor-form
  (editor-form-fn annotator/script-annotation-component ::script-spec/script))

(def mwe-editor-form
  (editor-form-fn annotator/mwe-annotation-component ::script-spec/mwe-script))
